"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.trackerLabelSchema = exports.taskTrackerValuesSchema = exports.taskFacetsSchema = exports.taskHistoryEntrySchema = exports.updateTaskCommentRequestSchema = exports.createTaskCommentRequestSchema = exports.taskCommentSchema = exports.commentMentionSchema = exports.COMMENT_MAX_MENTIONS = exports.createTaskDependencyRequestSchema = exports.taskDependencyEdgeSchema = exports.taskDependencySchema = exports.createTaskLinkRequestSchema = exports.taskLinkSchema = exports.updateTaskRequestSchema = exports.bulkDeleteSchema = exports.bulkTaskEditSchema = exports.createTaskRequestSchema = exports.taskSchema = exports.INTERNAL_KEY_INFIX = exports.TASK_KEY_PATTERN = exports.TAG_PATTERN = exports.updateTaskPriorityRequestSchema = exports.createTaskPriorityRequestSchema = exports.taskPrioritySchema = exports.deleteWithReassignRequestSchema = exports.reorderRequestSchema = exports.updateTaskStatusRequestSchema = exports.createTaskStatusRequestSchema = exports.taskStatusSchema = exports.STATUS_CATEGORIES = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const bulk_1 = require("../bulk");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Tasks, and the two vocabularies a workspace configures for them.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: one schema per shape, defined once; the server validates with it and the client
 * infers from it.
 */
const colorSchema = zod_1.z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/* ── statuses ────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **Four kinds, fixed — while the statuses themselves are rows a workspace edits.** A workspace
 * may invent *"Ügyfélre vár"*; it may not invent a fifth kind, because the board groups by this,
 * reports count `closed`, the timeline treats open and closed differently, and earned value weighs
 * each kind (`EARNED_WEIGHT`).
 *
 * ⚠⚠ **`in_review` IS THE ONE THE WEIGHTS ASKED FOR.** `0015` shipped three, so *Fejlesztés alatt*
 * and *Ellenőrzés alatt* earned the same 0.5 — and work that is built and waiting on a review is
 * worth more than work somebody has just picked up. `0141` adds it; see that file for why the
 * weight stays in our source rather than becoming a column a workspace edits.
 *
 * ⚠️ **ORDER IS MEANING HERE.** The array is the board's left-to-right and the enum's sort order,
 * so `in_review` sits between `in_progress` and `closed` and a reordering would silently move every
 * grouped list in the product.
 */
exports.STATUS_CATEGORIES = ['open', 'in_progress', 'in_review', 'closed'];
exports.taskStatusSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `task_status_1a2b3c4d` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('taskStatus'),
    name: zod_1.z.string(),
    color: colorSchema,
    category: zod_1.z.enum(exports.STATUS_CATEGORIES),
    position: zod_1.z.number().int(),
    isDefault: zod_1.z.boolean(),
    /** How many tasks currently sit in it. The settings screen shows it before offering a delete. */
    taskCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createTaskStatusRequestSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(60),
    color: colorSchema,
    category: zod_1.z.enum(exports.STATUS_CATEGORIES),
});
exports.updateTaskStatusRequestSchema = exports.createTaskStatusRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ⚠️ **Reorder is the WHOLE list of ids, not a pair.** A drag produces one new order; sending
 * `{ moved, before }` makes the server re-derive what the screen already knows, and the two can
 * disagree when a row was added by somebody else in between. The server rejects a list that is not
 * a permutation of what it holds, which is what makes a stale drag a refusal rather than a
 * silently partial write.
 */
exports.reorderRequestSchema = zod_1.z.object({
    ids: zod_1.z.array(zod_1.z.string()).min(1),
});
/**
 * ⚠️ **Deleting a status REQUIRES somewhere for its tasks to go.** The mockup's confirm dialog
 * already picks a target (`statusDelete.target`) and states the count. A delete that orphaned tasks
 * would leave rows pointing at nothing, and the FK is `restrict` so it would simply fail with a
 * constraint name.
 */
exports.deleteWithReassignRequestSchema = zod_1.z.object({
    reassignToId: zod_1.z.string(),
});
/* ── priorities ──────────────────────────────────────────────────────────────────────────── */
exports.taskPrioritySchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `task_priority_5e6f7a8b` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('taskPriority'),
    name: zod_1.z.string(),
    color: colorSchema,
    /** ⚠️ Ascending position is DESCENDING urgency — the mockup's list starts at Sürgős. */
    position: zod_1.z.number().int(),
    isDefault: zod_1.z.boolean(),
    taskCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createTaskPriorityRequestSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(60),
    color: colorSchema,
});
exports.updateTaskPriorityRequestSchema = exports.createTaskPriorityRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/* ── tasks ───────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **The one statement of what a tag may be** — `0243`'s CHECK says the same. Exported because the
 * sync turns labels into tags (`0160`) and a label the database would refuse has to be skipped
 * BEFORE the insert, or one odd label fails the whole issue's write.
 *
 * ⚠️⚠️ **NO LONGER LOWER-CASE, AND J-6 IS WHY.** `Backend` and `backend` are two labels in Jira, so a
 * tag folded to lower case could never be written back without creating a duplicate in the
 * customer's tracker. A tag on a task WITH an issue link is the tracker's label in the tracker's
 * spelling; a tag on a task without one is still folded by `TaskService`, as it always was. What the
 * shape refuses is only what nothing can hold: empty, over 80, a control character, whitespace at
 * either end.
 */
exports.TAG_PATTERN = /^\S(?:[^\p{Cc}]{0,78}\S)?$/u;
const tagSchema = zod_1.z
    .string()
    .regex(exports.TAG_PATTERN, 'a tag is 1–80 characters with no leading or trailing space');
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ J-7 · what a task key looks like, and whose it is.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A task WITH an issue link carries the tracker's key, spelled as the tracker spells it: `CEG-482`,
 * `AUCHSUP-9`, a YouTrack `support_2-14`. A task without one carries Forecastify's own `PREFIX-n` —
 * or **`PREFIX-INT-n`** once a tracker project anywhere in the workspace has taken that prefix, so
 * `HSO-51` can only ever mean the Jira issue. `0244`'s CHECK says the same; the two must agree.
 *
 * ⚠️ **Compared without regard to case** — `/tasks/hso-51` finds `HSO-51` as it always has, and a
 * lower-case tracker key is still one key per workspace.
 */
exports.TASK_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]*(?:-INT)?-[0-9]+$/;
/** The infix that marks a Forecastify-only task in a prefix a tracker has taken: `HSO-INT-51`. */
exports.INTERNAL_KEY_INFIX = 'INT';
/**
 * Everything a task row carries, with its references resolved for display.
 *
 * ⚠️ **The names and colours are resolved SERVER-SIDE**, not looked up by the screen from a
 * separate statuses call. A list of 200 tasks rendering a status colour must not depend on a second
 * request having landed — that is how a table renders grey for a second and then repaints.
 */
exports.taskSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /**
     * ⚠️ `PREFIX-n` for a task of ours, the tracker's own key for a synced one (J-7, `0244`). Never
     * changed by a move — and changed by the TRACKER: when an issue is created over there for a task
     * made here, when Jira renames a project or moves an issue. `TASK_KEY_PATTERN` is the shape.
     */
    key: zod_1.z.string(),
    /**
     * J-7 · `true` while this task's key is Forecastify's provisional one and an issue is being
     * created for it: the push is queued, or the claim is in flight. The tracker's key replaces it
     * when the create answers, and the screen is live, so the reader sees the swap.
     */
    keyPending: zod_1.z.boolean(),
    title: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: colorSchema.nullable(),
    statusId: zod_1.z.string(),
    statusName: zod_1.z.string(),
    statusColor: colorSchema,
    statusCategory: zod_1.z.enum(exports.STATUS_CATEGORIES),
    priorityId: zod_1.z.string(),
    priorityName: zod_1.z.string(),
    priorityColor: colorSchema,
    assigneeMemberId: zod_1.z.string().nullable(),
    assigneeName: zod_1.z.string().nullable(),
    /** C22 · the assignee's picture. See `personSchema.avatarUrl`. */
    assigneeAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    assigneeAvatarSeed: zod_1.z.string().nullable(),
    /**
     * C25 · which line item this task's hours belong to, and therefore which contract.
     *
     * ⚠️⚠️ **NULL IS ORDINARY, AND SINCE 8 Sep 2026 IT IS ALSO UNCONDITIONAL.**
     * `contracts.line_item_required` used to make one mandatory on create (P2-4) and is gone: it made
     * a task impossible to write down in a workspace that had not yet modelled its contracts, which is
     * every workspace on its first day. Work attached to no line item is a WARNING ON THE PROJECT in
     * Phase 5, derived from this column being null — never a refusal.
     */
    lineItemId: zod_1.z.string().nullable(),
    /** Resolved for the row, so a list does not need the line items to render one. */
    lineItemName: zod_1.z.string().nullable(),
    /** The contract the line item belongs to — what somebody actually recognises. */
    contractCode: zod_1.z.string().nullable(),
    /**
     * Set on the single **collector task** of a recurring contract (P2-10, migration `0054`).
     *
     * ⚠️ **Different from `lineItemId`, and both can be set.** `lineItemId` says whose budget the
     * hours come off; this says *this task exists because a contract recurs* — which is what lets the
     * contract find its own hours, and what stops somebody deleting it by accident.
     */
    recurringContractId: zod_1.z.string().nullable(),
    /**
     * Who decided the line item — C26, migration `0055`.
     *
     * ⚠️ **`rule` links belong to the engine.** It adds and drops them freely; a `manual` link it
     * never touches. The task detail shows the difference so nobody wonders why an assignment they
     * did not make appeared, or why one they did make survived a rule change.
     */
    lineItemSource: zod_1.z.enum(['manual', 'rule']).nullable(),
    /** ⚠️ A STRING, because `numeric` crosses the wire as one — see `hard-won.md`'s driver rows. */
    estimateHours: zod_1.z.string(),
    startsOn: wireDateSchema.nullable(),
    dueOn: wireDateSchema.nullable(),
    parentTaskId: zod_1.z.string().nullable(),
    parentKey: zod_1.z.string().nullable(),
    tags: zod_1.z.array(zod_1.z.string()),
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ J-1 · what the tracker files the issue under, read-only.                   │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * Jira's components, fix versions and epic, as the last sync read them (`0160` keeps them on the
     * issue link). ⚠️ **Null on a task with no tracker link** — the words mean nothing for work typed
     * into Forecastify. A YouTrack link carries two empty lists and no epic, which is the truth.
     *
     * ⚠️⚠️ **NAMES, NOT IDS, FOR THE TWO LISTS.** A component is an id per Jira project, and `Backend`
     * in two projects is two ids; the person filtering means the word. Rules still compare ids.
     * The epic is one issue, so it keeps its id, which is what the filter sends.
     */
    tracker: zod_1.z
        .object({
        components: zod_1.z.array(zod_1.z.string()),
        fixVersions: zod_1.z.array(zod_1.z.string()),
        /** `HSO-12 · Search`: the epic's key and summary. Never the task's own epic when it is one. */
        epic: zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() }).nullable(),
        /**
         * ⚠️⚠️ **J-2 · THE TRACKER'S STATUS, WHEN NOTHING MAPS IT — AND THEN THE STATUS IS LOCKED.**
         * The task sits on the workspace's first *in progress* status (or stays in one it is already
         * in) and the server refuses a status change with `task_status_unmapped`: there is no
         * honest answer to what Forecastify's status means while the tracker's has no counterpart.
         * Null when the tracker's status is mapped, or not known yet. Mapping it moves every such
         * task at once (`RestatusService`).
         */
        unmappedStatus: zod_1.z.string().nullable(),
    })
        .nullable(),
    /**
     * ⚠️ **The metafield values, resolved into the task read.** A detail view that had to make a
     * second request for them would render the form and then repopulate it, which reads as the values
     * arriving late — and a list showing an `in_list` metafield column cannot wait at all.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    /** Subtask progress, so a list row can render it without a second query. */
    subtaskCount: zod_1.z.number().int().nonnegative(),
    subtaskDoneCount: zod_1.z.number().int().nonnegative(),
    /**
     * Hours logged against this task, all time. A `numeric` string; render through `formatHours`.
     *
     * ⚠️⚠️ **HERE BECAUSE THE WORKLOG LIST NOW PAGES.** The Idolog tab used to sum the entries it
     * held, which was exact only because every entry arrived. With a cursor that sum silently becomes
     * *"the first fifty"* presented as a total — `state-rules.md` §5 forbids exactly that, and
     * `pagination.ts` explains why the envelope carries no count of its own: a screen that needs a
     * total asks for the **aggregate**, and this is that aggregate.
     *
     * ⚠️ Summed in SQL by `task_tracked_hours()`, the function 0017 added for the three chunks that
     * ask the same question.
     */
    trackedHours: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
exports.createTaskRequestSchema = zod_1.z
    .object({
    title: zod_1.z.string().min(1).max(300),
    description: zod_1.z.string().max(20000).nullable().optional(),
    /**
     * ⚠️ **A consistency check, not a choice** (P2-32.2): the project is the line item's contract's.
     * Sent, it must agree; absent, it follows from the line item.
     */
    projectId: zod_1.z.uuid().optional(),
    /** Absent means the workspace's default status — which is what a default is for. */
    statusId: zod_1.z.uuid().optional(),
    priorityId: zod_1.z.uuid().optional(),
    assigneeMemberId: zod_1.z.uuid().nullable().optional(),
    /**
     * ⚠️⚠️ **REQUIRED — P2-32.2.** Every task is created on a line item. The one exception is a
     * subtask, which inherits its parent's when it names none; the refine below says so.
     */
    lineItemId: zod_1.z.uuid().optional(),
    /** Accepts the hour forms the parser takes; the server normalises. */
    estimateHours: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
    startsOn: wireDateSchema.nullable().optional(),
    dueOn: wireDateSchema.nullable().optional(),
    parentTaskId: zod_1.z.uuid().nullable().optional(),
    tags: zod_1.z.array(tagSchema).max(20).optional(),
    /**
     * ⚠️ **Metafield values travel WITH the task, keyed by the field's `key`.** C10: a task's
     * *Számlázható* is part of editing that task, so it takes `task.update` — not
     * `settings.manage`, which is what defining the field takes. A separate endpoint would need its
     * own permission story and the honest answer is *"whatever the owning row requires"*.
     *
     * ⚠️ `z.unknown()` per value: nine types cannot share one Zod shape, and `validateValues` in
     * `@forecastify/shared` is the one implementation of the rules. Validating here as well would
     * put them in a second place.
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .refine((value) => value.startsOn == null || value.dueOn == null || value.dueOn >= value.startsOn, {
    path: ['dueOn'],
    message: 'the due date cannot precede the start',
})
    .refine((value) => value.lineItemId !== undefined || value.parentTaskId != null, {
    path: ['lineItemId'],
    message: 'a task needs a line item',
});
/**
 * ⚠️ **`key` is ABSENT, like a project's prefix and a client's code** — and for a stronger reason
 * than either: a task key is what people put in commit messages and emails. Renaming it invalidates
 * references this database cannot see.
 *
 * ⚠️ **Every field is optional, because the detail view saves ONE at a time.** The mockup's
 * `taskPatch(k, { st: … })` is the whole interaction model: a status pick, an estimate blur, a tag
 * added. Requiring the full object would make each of those send back fields the caller never
 * looked at, and the last screen to load would win.
 */
/**
 * A bulk edit from the list — item 72.
 *
 * ⚠️⚠️ **THE THREE FIELDS THE LIST CAN SET, AND NOT ONE MORE.** A bulk `title` or `description`
 * would write the same sentence onto forty tasks, which is never what somebody means; a bulk
 * `estimateHours` is a number that belongs to one task's scope. Status, priority and assignee are
 * the three that are genuinely properties of a SELECTION — *"these are all done"*, *"these are all
 * urgent"*, *"these are all Anna's"*.
 *
 * ⚠️ **`assigneeMemberId` is NULLABLE and the null means unassign**, which is the operation a
 * `.optional()` alone could not express: absent is *"leave it"* and `null` is *"clear it"*.
 */
exports.bulkTaskEditSchema = zod_1.z.object({
    targets: bulk_1.bulkTargetsSchema,
    statusId: zod_1.z.uuid().optional(),
    priorityId: zod_1.z.uuid().optional(),
    assigneeMemberId: zod_1.z.uuid().nullable().optional(),
});
exports.bulkDeleteSchema = zod_1.z.object({ targets: bulk_1.bulkTargetsSchema });
exports.updateTaskRequestSchema = zod_1.z
    .object({
    title: zod_1.z.string().min(1).max(300).optional(),
    description: zod_1.z.string().max(20000).nullable().optional(),
    /** ⚠️ A consistency check only (P2-32.2) — a task moves project by moving line item. */
    projectId: zod_1.z.uuid().optional(),
    statusId: zod_1.z.uuid().optional(),
    priorityId: zod_1.z.uuid().optional(),
    assigneeMemberId: zod_1.z.uuid().nullable().optional(),
    /** ⚠️ Changeable, never clearable (P2-32.2). */
    lineItemId: zod_1.z.uuid().optional(),
    estimateHours: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
    startsOn: wireDateSchema.nullable().optional(),
    dueOn: wireDateSchema.nullable().optional(),
    parentTaskId: zod_1.z.uuid().nullable().optional(),
    tags: zod_1.z.array(tagSchema).max(20).optional(),
    /** Absent means "leave them"; a present map REPLACES the lot — see the create note. */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().nonnegative(),
})
    .refine((value) => value.startsOn == null || value.dueOn == null || value.dueOn >= value.startsOn, {
    path: ['dueOn'],
    message: 'the due date cannot precede the start',
});
/* ── links, dependencies, comments ───────────────────────────────────────────────────────── */
/** A symmetric "relates to". No direction, no weight — see 0015 on why it is not a dependency. */
exports.taskLinkSchema = zod_1.z.object({
    id: zod_1.z.string(),
    taskId: zod_1.z.string(),
    key: zod_1.z.string(),
    title: zod_1.z.string(),
    statusName: zod_1.z.string(),
    statusColor: colorSchema,
});
exports.createTaskLinkRequestSchema = zod_1.z.object({ taskId: zod_1.z.uuid() });
/**
 * A directed edge with a weight: *this starts after that finishes, plus `lagDays`*.
 *
 * ⚠️ `direction` says which side the task being viewed is on, so one list can show both *"waits
 * for"* and *"is waited on by"* without the screen re-deriving it from two ids.
 */
exports.taskDependencySchema = zod_1.z.object({
    id: zod_1.z.string(),
    direction: zod_1.z.enum(['predecessor', 'successor']),
    taskId: zod_1.z.string(),
    key: zod_1.z.string(),
    title: zod_1.z.string(),
    statusName: zod_1.z.string(),
    statusColor: colorSchema,
    /** May be negative — *"start two days before it finishes"* is ordinary in a plan. */
    lagDays: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
/**
 * One dependency edge, by KEY, for the timeline.
 *
 * ⚠️⚠️ **KEYS, NOT IDS, BECAUSE THAT IS WHAT THE GANTT ADDRESSES ROWS BY.** `GanttRow.key` is the
 * task key and `GanttDependency.key` names the predecessor's row; handing the component ids would
 * make every caller join them back to keys before it could draw a single arrow.
 *
 * ⚠️ **This is a FLAT EDGE LIST, not `taskDependencySchema` per task.** That shape carries the
 * other end's title, status and colour because the detail draws a row for it; the timeline already
 * has all of that from the task it is drawing. Reusing it would fetch the same rows twice — once as
 * tasks, once as the far side of their own edges.
 *
 * ⚠️ **`violated` is NOT here, deliberately.** Whether an edge is broken is arithmetic on two
 * dates the client is already holding, and it changes while somebody drags a bar — a server-side
 * flag would be stale the moment the drag started.
 */
exports.taskDependencyEdgeSchema = zod_1.z.object({
    predecessorKey: zod_1.z.string(),
    successorKey: zod_1.z.string(),
    lagDays: zod_1.z.number().int(),
});
exports.createTaskDependencyRequestSchema = zod_1.z.object({
    /** The task that must finish first. */
    predecessorId: zod_1.z.uuid(),
    lagDays: zod_1.z.number().int().min(-365).max(365).optional(),
});
/**
 * How many people one comment may mention.
 *
 * ⚠️ **A cap, because a mention is an email.** Twenty is more than any real comment and few
 * enough that "@everyone by hand" is not a way to mail the whole workspace from a text box.
 */
exports.COMMENT_MAX_MENTIONS = 20;
/**
 * One resolved mention.
 *
 * ⚠️⚠️ **THE NAME IS JOINED AT READ TIME, NOT STORED WITH THE MENTION** — R-16 asks for exactly
 * this: *"a stored reference so a renamed member's mention still resolves"*. `0026` keeps the member
 * id; this carries the name as it is now, so the client can highlight the text and link the person
 * without the reference and the label ever being two copies of one fact.
 */
exports.commentMentionSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    displayName: zod_1.z.string(),
});
exports.taskCommentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `task_comment_9c0d1e2f` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('taskComment'),
    authorMemberId: zod_1.z.string(),
    authorName: zod_1.z.string(),
    /** C22 · the author's picture, beside their name on the comment. See `personSchema.avatarUrl`. */
    authorAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    authorAvatarSeed: zod_1.z.string().nullable(),
    /** Sanitised on the way IN — see 0015. A reader can trust it. */
    bodyHtml: zod_1.z.string(),
    /**
     * Who this comment mentions.
     *
     * ⚠️ **The `@name` in `bodyHtml` is PLAIN TEXT**, because the sanitiser drops every attribute
     * and widening it is what `0026` argues against. This list is the machine-readable half: the
     * client highlights the text by matching these names, and the server notified these people.
     */
    mentions: zod_1.z.array(exports.commentMentionSchema),
    createdAt: zod_1.z.string(),
    editedAt: zod_1.z.string().nullable(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️⚠️ **THE MENTIONS ARE SENT AS IDS ALONGSIDE THE BODY, NOT PARSED OUT OF IT.** Reading
 * `@Kovács Anna` back out of the text on the server would mean matching display names — which are
 * not unique, contain spaces, and change. The composer knows exactly who was picked; sending that is
 * the only version of this that is right for two people with the same name.
 *
 * ⚠️ **The server does not require the name to appear in the body.** It could check, and the check
 * would fail the moment somebody edits the text around a mention — which is a normal thing to do to
 * a sentence, and would turn a typo fix into a rejected save.
 */
exports.createTaskCommentRequestSchema = zod_1.z.object({
    bodyHtml: zod_1.z.string().min(1).max(20000),
    mentionMemberIds: zod_1.z.array(zod_1.z.string()).max(exports.COMMENT_MAX_MENTIONS).optional(),
});
exports.updateTaskCommentRequestSchema = zod_1.z.object({
    bodyHtml: zod_1.z.string().min(1).max(20000),
    mentionMemberIds: zod_1.z.array(zod_1.z.string()).max(exports.COMMENT_MAX_MENTIONS).optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/* ── history ──────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **The task's history is the AUDIT LOG, read back — not a second table.** CLAUDE.md rule 4
 * already requires every mutation to be audited with actor, before/after diff and request id, in
 * the same transaction. A parallel `task_events` table would be that data written twice, and the
 * copy that drifts is the one the screen shows.
 *
 * ⚠️ **`before` and `after` are the CHANGED FIELDS ONLY**, in the database's vocabulary
 * (`status_id`, not `statusId`) — migration 0006 stores the diff rather than whole rows. The screen
 * renders field names through the catalogue; anything it cannot name renders as the raw key in
 * mono, which the design system already prescribes for machine identifiers.
 */
exports.taskHistoryEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    action: zod_1.z.string(),
    actorName: zod_1.z.string().nullable(),
    at: zod_1.z.string(),
    before: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
    after: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — the statuses, priorities and projects the tasks a list's filters match
 * actually carry (`GET /tasks/facets`), each in the workspace's own words. The person page's task
 * filters, and the projects they have work on, without reading every task to find them.
 */
exports.taskFacetsSchema = zod_1.z.object({
    statuses: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
    priorities: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
    projects: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
});
/**
 * J-1 · the values the task list can filter its tracker facts by: what the synced tasks in reach
 * actually carry, never the tracker's whole catalogue — a filter offering a value no task has
 * answers *"there are none"* to a question nobody needed to ask.
 */
exports.taskTrackerValuesSchema = zod_1.z.object({
    components: zod_1.z.array(zod_1.z.string()),
    fixVersions: zod_1.z.array(zod_1.z.string()),
    epics: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
});
/**
 * J-6 · one label the tracker offers — `GET /tasks/:id/tracker-labels`, the tag picker on a task
 * with an issue link. `id` is the name on Jira and the tag's id on YouTrack; `name` is what a
 * person picks and what the tag stores, in the tracker's own spelling. Empty on an unlinked task.
 */
exports.trackerLabelSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
});
//# sourceMappingURL=contract.js.map